import { pdfTemplateDesignSchema, type PdfTemplateDesign } from "@workspace/api-zod/pdf-template";

const key = (userId: string) => `tpt:pdf-design-draft:${encodeURIComponent(userId)}`;
type Draft = { design: PdfTemplateDesign; baselineVersion: string | null; updatedAt: number };

/** Browser drafts are recovery copies, never saved company defaults. */
export function readDesignDraft(userId: string, storage: Storage): Draft | null {
  try {
    const raw = JSON.parse(storage.getItem(key(userId)) || "null");
    if (!raw || typeof raw.updatedAt !== "number" || Date.now() - raw.updatedAt > 30 * 86400_000) return null;
    const parsed = pdfTemplateDesignSchema.safeParse(raw.design);
    return parsed.success ? { design: parsed.data, baselineVersion: typeof raw.baselineVersion === "string" ? raw.baselineVersion : null,
      updatedAt: raw.updatedAt } : null;
  } catch { return null; }
}

export function writeDesignDraft(userId: string, design: PdfTemplateDesign, baselineVersion: string | null, storage: Storage): boolean {
  try {
    const parsed = pdfTemplateDesignSchema.safeParse(design);
    if (!parsed.success) return false;
    storage.setItem(key(userId), JSON.stringify({ design: parsed.data, baselineVersion, updatedAt: Date.now() }));
    return true;
  } catch { return false; }
}

export function clearDesignDraft(userId: string, storage: Storage) {
  try { storage.removeItem(key(userId)); } catch { /* Saving on the server still works without browser storage. */ }
}

export function isPreviewAssetFailure(error: unknown): boolean {
  return error instanceof Error && /failed to fetch dynamically imported module|importing a module script failed|error loading dynamically imported module|failed to load module script/i.test(error.message);
}

/** A removed deployment chunk cannot be retried at the same URL. Reload only
 * after preserving the draft, at most once per app release in this tab. */
export function recoverPreviewAssets(userId: string, design: PdfTemplateDesign, version: string | null,
  release: string, storage: Storage, session: Storage, reload: () => void): boolean {
  try {
    const attempt = `tpt:pdf-preview-recovery:${release}:${userId}`;
    if (session.getItem(attempt) || !writeDesignDraft(userId, design, version, storage)) return false;
    session.setItem(attempt, "1");
    reload();
    return true;
  } catch { return false; }
}
